import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { EMT_PAGE_NAMES } from '../../constants';
import { generateTags } from '../../utils/generate-tags';
import { emtContentModel } from '../../models';
import {
  generateLocalCityCanonical,
  generateLocalCityDesc,
  generateLocalCityTitle,
  getLocalCityPreloadImages,
  getLocalCitySchemaTags
} from './local-city-helpers';

// --- Tag Handler ---

const localCityHandler = ({
  data: { emtData },
  props: { city, guid, state }
}) => {
  // Extract emtContent data once
  const emtContent = emtData?.emtContent?.content || {};
  const emtContentMetadata = emtContent?.metadata || {};
  const storeFinderPromotions = emtContent?.storeFinderPromotions || {};

  const fallbackTitle = generateLocalCityTitle({ city, state });
  const fallbackDescription = generateLocalCityDesc({ city, state });
  const {
    metaDescription = fallbackDescription,
    ogDescription = fallbackDescription,
    ogImage,
    ogTitle = fallbackTitle,
    ogType = 'lcp-homepage',
    schema: emtSchema,
    title = fallbackTitle
  } = emtContentMetadata;

  const canonicalURL = generateLocalCityCanonical({ city, state, guid });

  const preloadImages = getLocalCityPreloadImages(storeFinderPromotions);

  const schemas = getLocalCitySchemaTags({ emtSchema, city, state, guid });

  const tags = generateTags({
    canonical: canonicalURL,
    description: metaDescription,
    ogDescription,
    ogSiteName: 'The Home Depot',
    ogImage,
    ogTitle,
    ogType,
    ogURL: canonicalURL,
    preloadImages,
    schemas,
    title
  });

  return tags;
};

// --- Main Component ---

const LocalCityMetadata = ({ city, state, guid }) => {
  const emtContentOptions = {
    variables: {
      contentId: guid,
      pageName: EMT_PAGE_NAMES.LOCAL_CITY
    }
  };

  const { data: emtData } = useDataModel('emtContent', emtContentOptions);

  const data = { emtData };
  const props = { city, guid, state };

  useHelmet('localCityMetadata', { data, props }, localCityHandler, [guid]);

  return null;
};

// --- Component Static Properties (dataModel, propTypes, etc.) ---

LocalCityMetadata.displayName = 'LocalCityMetadata';

LocalCityMetadata.dataModel = extend(
  emtContentModel
);

LocalCityMetadata.propTypes = {
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  guid: PropTypes.string.isRequired
};

export { LocalCityMetadata };